import { useEffect } from "react";
import { Table, Pagination } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useState } from "react";
import { getOrgKycUsersListApi } from "../../../../redux/action/Kyc-Users";
import { kycStatusBadge, cardStatusBadge } from "./KycUsersTableConfig";
import useIsMobile from "../shared/Mobile-Responsive";

const KycUsersTable = ({ searchTerm, filters, currentPage, setCurrentPage }) => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [pageSize, setPageSize] = useState(10);
  const [pageDropdownOpen, setPageDropdownOpen] = useState(false);

  const pageSizeOptions = [10, 20, 50, 100];

  const { orgKycUsersList, orgKycUsersListLoading, orgKycUsersTotal } = useSelector(
    (state) => state.ORG_KYC_USERS
  );

  useEffect(() => {
    dispatch(
      getOrgKycUsersListApi({
        page: currentPage - 1,
        size: pageSize,
        name: searchTerm || undefined,
        kycStatus: filters?.kycStatus || undefined,
        cardStatus: filters?.cardStatus || undefined,
      })
    );
  }, [dispatch, currentPage, pageSize, searchTerm, filters]);

  const formattedData = Array.isArray(orgKycUsersList)
    ? orgKycUsersList.map((item, index) => ({
        key: item.userId || item.pcUserId || index,
        name: item.name || "NA",
        email: item.email || "NA",
        mobile: item.mobile || "NA",
        kycStatus: item.kycStatus || "NOT_APPLIED",
        cardStatus: item.cardStatus || "NOT_APPLIED",
        kycFailureReason: item.kycFailureReason || "-",
        kycUpdatedAt: item.kycUpdatedAt
          ? new Date(item.kycUpdatedAt).toLocaleDateString()
          : "-",
      }))
    : [];

  const KycUserCard = ({ user }) => (
    <div className="bg-white rounded-2xl border-[1px] border-solid border-[#E2E8F0] shadow-sm px-4 py-3 mb-3 font-poppins">
      <div className="space-y-3">
        <div className="flex justify-between">
          <span className="text-[11px] font-semibold text-[#64748B] uppercase tracking-wide">NAME</span>
          <span className="text-[13px] font-medium text-[#171717]">{user.name}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[11px] font-semibold text-[#64748B] uppercase tracking-wide">EMAIL</span>
          <span className="text-[13px] font-medium text-[#171717]">{user.email}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[11px] font-semibold text-[#64748B] uppercase tracking-wide">MOBILE</span>
          <span className="text-[13px] font-medium text-[#171717]">{user.mobile}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[11px] font-semibold text-[#64748B] uppercase tracking-wide">KYC STATUS</span>
          {kycStatusBadge(user.kycStatus)}
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[11px] font-semibold text-[#64748B] uppercase tracking-wide">CARD STATUS</span>
          {cardStatusBadge(user.cardStatus)}
        </div>
        <div className="flex justify-between">
          <span className="text-[11px] font-semibold text-[#64748B] uppercase tracking-wide">UPDATED</span>
          <span className="text-[13px] font-medium text-[#171717]">{user.kycUpdatedAt}</span>
        </div>
      </div>
    </div>
  );

  const columns = [
    { title: "NAME", dataIndex: "name", key: "name" },
    { title: "EMAIL", dataIndex: "email", key: "email" },
    { title: "MOBILE", dataIndex: "mobile", key: "mobile" },
    {
      title: "KYC STATUS",
      dataIndex: "kycStatus",
      key: "kycStatus",
      render: kycStatusBadge,
    },
    {
      title: "CARD STATUS",
      dataIndex: "cardStatus",
      key: "cardStatus",
      render: cardStatusBadge,
    },
    { title: "UPDATED", dataIndex: "kycUpdatedAt", key: "kycUpdatedAt" },
    { title: "FAILURE REASON", dataIndex: "kycFailureReason", key: "kycFailureReason" },
  ];

  const PaginationBar = () => (
    <div className={`flex ${isMobile ? "flex-col pt-0 pb-3" : "flex-row py-3"} justify-between items-center px-3 gap-4 w-full`}>
      <div className="flex items-center gap-4">
        <span className={`${isMobile ? "text-xs" : "text-sm"} text-[#64748B] font-poppins`}>Show on page</span>

        <div className="relative flex-shrink-0">
          <button
            onClick={() => setPageDropdownOpen(!pageDropdownOpen)}
            className="flex items-center gap-2 h-9 px-[12px] border-[1px] border-solid border-[#E2E8F0] bg-white rounded-xl shadow-sm text-[13px] text-[#171717] font-poppins font-semibold min-w-max hover:border-[#CBD5E1]"
          >
            {pageSize} Rows
            {pageDropdownOpen ? (
              <FiChevronUp className="w-4 h-4" />
            ) : (
              <FiChevronDown className="w-4 h-4" />
            )}
          </button>

          {pageDropdownOpen && (
            <div className="absolute bottom-full mb-2 w-32 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-md z-10 overflow-hidden">
              {pageSizeOptions.map((size) => (
                <div
                  key={size}
                  className="px-4 py-2 hover:bg-[#F8FAFC] cursor-pointer text-sm text-[#334155]"
                  onClick={() => {
                    setPageSize(size);
                    setCurrentPage(1);
                    setPageDropdownOpen(false);
                  }}
                >
                  {size} Rows
                </div>
              ))}
            </div>
          )}
        </div>

        <span className={`${isMobile ? "text-xs" : "text-sm"} text-[#64748B] font-poppins`}>
          Showing {orgKycUsersTotal === 0 ? 0 : (currentPage - 1) * pageSize + 1} to{" "}
          {Math.min(currentPage * pageSize, orgKycUsersTotal)} of {orgKycUsersTotal} entries
        </span>
      </div>

      <Pagination
        current={currentPage}
        total={orgKycUsersTotal}
        pageSize={pageSize}
        onChange={(page) => setCurrentPage(page)}
        showSizeChanger={false}
        itemRender={(page, type, originalElement) => {
          if (type === "page") {
            return (
              <button
                className={`flex items-center justify-center h-8 px-3 rounded-lg text-sm font-semibold ${
                  page === currentPage
                    ? "bg-[#2563EB] text-white border-0"
                    : "bg-white text-[#334155] border-[1px] border-solid border-[#E2E8F0]"
                }`}
              >
                {page}
              </button>
            );
          }
          if (type === "prev") {
            return <button className="bg-white border-0 font-semibold text-sm text-[#2563EB]">Previous</button>;
          }
          if (type === "next") {
            return <button className="bg-white border-0 font-semibold text-sm text-[#2563EB]">Next</button>;
          }
          return originalElement;
        }}
      />
    </div>
  );

  return (
    <div className="px-2 font-poppins bg-white rounded-2xl overflow-y-auto">
      <div className="hidden md:block">
        <Table
          loading={orgKycUsersListLoading}
          columns={columns}
          dataSource={formattedData}
          pagination={false}
          bordered={false}
          className="font-poppins member-table"
          rowClassName={(_, index) =>
            index % 2 === 0
              ? "bg-white transition"
              : "bg-[#F8FAFC] transition"
          }
        />
      </div>

      <div className="block md:hidden">
        <div className="p-2">
          {formattedData.length > 0 ? (
            formattedData.map((user) => <KycUserCard key={user.key} user={user} />)
          ) : (
            <div className="text-center py-8 text-[#64748B] text-sm">No users found</div>
          )}
        </div>
      </div>

      <PaginationBar />
    </div>
  );
};

export default KycUsersTable;
